'use client';
import React, { useState } from 'react';
import { useSnapshot } from 'valtio';
import { appState } from '../store';
import useApi from '../hooks/useApi';
import { ApiError, NewUserRequest } from '../@backend';
import { Input } from '../ui/ds/Input';
import { Btn } from '../ui/ds/Btn';
import { Badge } from '../ui/ds/Badge';

export default function RegistrationModal() {
  const appSnap = useSnapshot(appState);
  const { backend } = useApi(true);

  const [firstname, setFirstname] = useState('');
  const [lastname,  setLastname]  = useState('');
  const [email,     setEmail]     = useState('');
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [terms,     setTerms]     = useState(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isSuccess,  setIsSuccess]  = useState(false);

  if (!appSnap.modals.showRegisterModal) return null;

  const close = () => { appState.modals.showRegisterModal = false; };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors([]);

    if (!terms) {
      setFormErrors(['Je potřeba souhlasit s podmínkami tipovací hry']);
      return;
    }
    if (password1.length < 4) {
      setFormErrors(['Heslo musí mít alespoň 4 znaky']);
      return;
    }
    if (password1 !== password2) {
      setFormErrors(['Hesla se neshodují']);
      return;
    }
    if (!firstname.trim() || !lastname.trim()) {
      setFormErrors(['Je potřeba vyplnit jméno a příjmení']);
      return;
    }

    try {
      const res = await backend.user.userControllerRegisterUser({
        email, password1, password2, firstname, lastname,
      } as NewUserRequest);

      if (res.id) {
        setIsSuccess(true);
        setTimeout(() => {
          close();
          appState.modals.showLoginModal = true;
          setFirstname(''); setLastname(''); setEmail('');
          setPassword1(''); setPassword2(''); setTerms(false);
          setIsSuccess(false);
        }, 2000);
      }
    } catch (e: any) {
      if (e.status === 409) {
        setFormErrors(['Uživatel s tímto emailem již existuje']);
      } else if (e instanceof ApiError && e.status === 422) {
        setFormErrors([e.body?.error?.details?.[0]?.message ?? 'Neplatná data']);
      } else {
        setFormErrors(['Registrace selhala. Zkuste to prosím znovu.']);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8 overflow-y-auto"
      onClick={close}
    >
      <div
        className="bg-bg-card rounded-hero shadow-card w-full max-w-md overflow-hidden my-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-header px-6 py-5">
          <h2 className="tb-h2 text-white">Registrace</h2>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-6 flex flex-col gap-1">
          {isSuccess && (
            <div className="flex items-center gap-3 bg-semantic-success/10 border border-semantic-success/20 rounded-content px-4 py-3 mb-2">
              <Badge variant="success">Hotovo</Badge>
              <span className="tb-body text-semantic-success">Registrace proběhla úspěšně! Nyní se můžete přihlásit.</span>
            </div>
          )}
          {formErrors.length > 0 && (
            <div className="flex items-center gap-3 bg-semantic-danger/10 border border-semantic-danger/20 rounded-content px-4 py-3 mb-2">
              <Badge variant="danger">Chyba</Badge>
              <span className="tb-body text-semantic-danger">{formErrors.join(', ')}</span>
            </div>
          )}

          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Jméno"
                type="text"
                value={firstname}
                onChange={e => setFirstname(e.target.value)}
                placeholder="Jan"
                required
              />
            </div>
            <div className="flex-1">
              <Input
                label="Příjmení"
                type="text"
                value={lastname}
                onChange={e => setLastname(e.target.value)}
                placeholder="Novák"
                required
              />
            </div>
          </div>

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="vas@email.cz"
            autoComplete="email"
            required
          />

          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Heslo"
                type="password"
                value={password1}
                onChange={e => setPassword1(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
              />
            </div>
            <div className="flex-1">
              <Input
                label="Heslo znovu"
                type="password"
                value={password2}
                onChange={e => setPassword2(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
              />
            </div>
          </div>

          <div className="flex items-start gap-2 mb-4 mt-1">
            <input
              id="terms"
              type="checkbox"
              checked={terms}
              onChange={e => setTerms(e.target.checked)}
              className="mt-1 w-4 h-4 accent-brand-blue flex-shrink-0"
            />
            <label htmlFor="terms" className="tb-caption text-text-muted leading-relaxed">
              Souhlasím s{' '}
              <a href="/pravidla" target="_blank" rel="noreferrer" className="text-brand-blue underline">
                pravidly tipovací soutěže
              </a>{' '}
              a podmínkami pro{' '}
              <a href="/gdpr" target="_blank" rel="noreferrer" className="text-brand-blue underline">
                zpracování osobních údajů
              </a>
            </label>
          </div>

          <div className="flex gap-3">
            <Btn type="button" variant="ghost" size="sm" fullWidth onClick={close}>
              Zrušit
            </Btn>
            <Btn type="submit" variant="primary" size="sm" fullWidth disabled={isSuccess}>
              Registrovat
            </Btn>
          </div>
        </form>
      </div>
    </div>
  );
}
